import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/api-client';
import { conversionRate } from '@/lib/metrics';

/**
 * The home page's « 7 derniers jours » figures (W4, B2-home, DES-19).
 *
 * They come from the server analytics (POST /api/analytics/kpi), whose
 * metric contracts are the ones every screen shares since W2: connected =
 * reached the agent (agent_connected_at), meeting = the meeting flag,
 * conversion = meetings / connected. The page counted them itself from the
 * dialer's capped call list, with its own « connected » rule.
 *
 * Two windows: the last 7 days and the 7 days before, for the trend. Both
 * are narrowed to the active customer (ADR-024).
 */

const DAY = 86_400_000;
const TZ = 'Europe/Paris';

export interface KpiWindow {
  total: number;
  connected: number;
  meetings: number;
  byDay?: Array<{ date: string; calls: number; connected: number; meetings: number }>;
}

export interface Metric { current: number | null; previous: number | null; kind: 'count' | 'rate' }

export interface WeekMetrics {
  calls: Metric;
  connected: Metric;
  meetings: Metric;
  conversion: Metric;
  /** One point per day of the current window, oldest first. */
  series: { calls: number[]; connected: number[]; meetings: number[] };
}

/** YYYY-MM-DD of an instant, in the server's time zone (the byDay keys). */
export function dayKey(ms: number): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ms));
}

/** Every day from `from` to `to` (inclusive), filled with 0 where the server sent none. */
export function dailySeries(byDay: KpiWindow['byDay'], from: number, to: number, field: 'calls' | 'connected' | 'meetings'): number[] {
  const index = new Map((byDay ?? []).map((d) => [d.date, d[field]]));
  const keys: string[] = [];
  for (let t = from; ; t += DAY) {
    const k = dayKey(Math.min(t, to));
    if (keys[keys.length - 1] !== k) keys.push(k);
    if (t >= to) break;
  }
  return keys.map((k) => index.get(k) ?? 0);
}

/**
 * The four tiles. `previous` is null when there is no earlier activity at all
 * (a new account): no trend then, instead of « +100 % » from nothing.
 */
export function buildWeekMetrics(current: KpiWindow, previous: KpiWindow | null, from: number, to: number): WeekMetrics {
  const prev = previous && previous.total > 0 ? previous : null;
  return {
    calls: { current: current.total, previous: prev ? prev.total : null, kind: 'count' },
    connected: { current: current.connected, previous: prev ? prev.connected : null, kind: 'count' },
    meetings: { current: current.meetings, previous: prev ? prev.meetings : null, kind: 'count' },
    conversion: {
      current: conversionRate(current.meetings, current.connected),
      previous: prev ? conversionRate(prev.meetings, prev.connected) : null,
      kind: 'rate',
    },
    series: {
      calls: dailySeries(current.byDay, from, to, 'calls'),
      connected: dailySeries(current.byDay, from, to, 'connected'),
      meetings: dailySeries(current.byDay, from, to, 'meetings'),
    },
  };
}

/** One window of POST /api/analytics/kpi (shared with the portal home, usePortalKpis). */
export async function fetchKpiWindow(from: number, to: number, customerId: string | null): Promise<KpiWindow> {
  const { data } = await api.functions.invoke<KpiWindow>('generateKPIReport', {
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
    source: 'all',
    ...(customerId ? { customer_id: customerId } : {}),
  });
  if (!data || typeof data.total !== 'number') throw new Error('analytics/kpi: unexpected answer');
  return data as KpiWindow;
}

export function useHomeWeekMetrics(customerId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['home-week-kpi', customerId ?? null],
    queryFn: async () => {
      const to = Date.now();
      const from = to - 7 * DAY;
      const [current, previous] = await Promise.all([
        fetchKpiWindow(from, to, customerId ?? null),
        fetchKpiWindow(from - 7 * DAY, from, customerId ?? null),
      ]);
      return buildWeekMetrics(current, previous, from, to);
    },
    enabled,
    staleTime: 60_000,
    retry: 1,
  });
}
